import React, { useEffect, useState } from 'react';
import { DrModelTestingService } from '../../../services/DrModelTestingService';
import './stats.css';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

interface GpaiStats {
  total_reviews: number;
  initial_validity_rate: string;
  triage_validity_rate: string;
  both_valid_cases: number;
  initial_invalid_triage_valid: number;
  initial_valid_triage_invalid: number;
  both_invalid_cases: number;
  average_review_duration_minutes: number;
  total_unique_doctors: number;
  reviews_per_doctor: {
    min: number;
    max: number;
    average: number;
  };
  total_meds_recommendations: number;
}

const COLORS = ['#60a5fa', '#64748b', '#fbbf24', '#ef4444'];
const PIE_COLORS = ['#6366f1', '#fbbf24', '#10b981', '#f87171']; // Indigo, yellow, green, red

const ProgressBar = ({ value, label }: { value: number; label: string }) => (
  <div className="progress-bar-container">
    <div className="progress-bar-label">{label}</div>
    <div className="progress-bar-bg">
      <div className="progress-bar-fill alive" style={{ width: `${value}%` }} />
      <span className="progress-bar-value">{value}%</span>
    </div>
  </div>
);

const GPAIStats: React.FC = () => {
  const [stats, setStats] = useState<GpaiStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    DrModelTestingService.getGpaiReviewStats()
      .then((statsData: GpaiStats) => setStats(statsData))
      .catch(() => setError('Failed to load GPAI stats'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="stats-dashboard">Loading...</div>;
  if (error) return <div className="stats-dashboard error">{error}</div>;
  if (!stats) return null;

  const reviewsPerDoctorData = [
    { name: 'Min', value: stats.reviews_per_doctor.min },
    { name: 'Avg', value: stats.reviews_per_doctor.average },
    { name: 'Max', value: stats.reviews_per_doctor.max },
  ];

  // Pie chart for case distribution
  const casePieData = [
    { name: 'Both Valid', value: stats.both_valid_cases },
    { name: 'Initial Invalid, Triage Valid', value: stats.initial_invalid_triage_valid },
    { name: 'Initial Valid, Triage Invalid', value: stats.initial_valid_triage_invalid },
    { name: 'Both Invalid', value: stats.both_invalid_cases },
  ];

  // Parse validity rates
  const initialRate = parseInt(stats.initial_validity_rate);
  const triageRate = parseInt(stats.triage_validity_rate);

  return (
    <div className="stats-dashboard enhanced">
      <h2>GPAI Review Statistics</h2>
      <div className="stats-section">
        <div className="stats-cards">
          <div className="stat-card">
            <div className="stat-icon">
              {/* Bar chart icon for total reviews */}
              <svg width="24" height="24" fill="none" viewBox="0 0 24 24"><rect x="3" y="13" width="4" height="8" rx="2" fill="#6366f1" /><rect x="10" y="9" width="4" height="12" rx="2" fill="#10b981" /><rect x="17" y="5" width="4" height="16" rx="2" fill="#fbbf24" /></svg>
            </div>
            <div className="stat-title">Total Reviews</div>
            <div className="stat-value">{stats.total_reviews}</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              {/* Clock icon for duration */}
              <svg width="24" height="24" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="#10b981" strokeWidth="2" fill="none" /><path d="M12 7v5l3 3" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" /></svg>
            </div>
            <div className="stat-title">Avg. Review Duration (min)</div>
            <div className="stat-value">{stats.average_review_duration_minutes}</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              {/* User/doctor icon */}
              <svg width="24" height="24" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="8" r="4" fill="#fbbf24" /><path d="M4 20c0-2.21 3.58-4 8-4s8 1.79 8 4" stroke="#6366f1" strokeWidth="2" /></svg>
            </div>
            <div className="stat-title">Total Unique Doctors</div>
            <div className="stat-value">{stats.total_unique_doctors}</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              {/* Pill/medication icon for meds recommendations */}
              <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
                <rect x="3" y="13" width="8" height="8" rx="4" fill="#10b981" />
                <rect x="13" y="3" width="8" height="8" rx="4" fill="#f87171" />
                <rect x="7" y="7" width="10" height="10" rx="5" fill="#fbbf24" fillOpacity=".7" />
                <path d="M8 16l8-8" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </div>
            <div className="stat-title">Total Meds Recommendations</div>
            <div className="stat-value">{stats.total_meds_recommendations}</div>
          </div>
        </div>
      </div>
      <div className="stats-section">
        <h3 className="stats-section-title">Validity Rates</h3>
        <div className="stats-cards validity-progress">
          <ProgressBar value={initialRate} label="Initial Validity" />
          <ProgressBar value={triageRate} label="Triage Validity" />
        </div>
      </div>
      <div className="stats-section">
        <h3 className="stats-section-title">Case Distribution</h3>
        <div className="case-distribution-layout">
          <div className="stat-card stat-chart-card pie-chart-card">
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={casePieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  label
                >
                  {casePieData.map((entry, idx) => (
                    <Cell key={`cell-${idx}`} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="stat-title">Cases</div>
          </div>
          <div className="case-cards-row">
            <div className="stat-card">
              <div className="stat-title">Both Valid</div>
              <div className="stat-value">{stats.both_valid_cases}</div>
            </div>
            <div className="stat-card">
              <div className="stat-title">Initial Invalid, Triage Valid</div>
              <div className="stat-value">{stats.initial_invalid_triage_valid}</div>
            </div>
            <div className="stat-card">
              <div className="stat-title">Initial Valid, Triage Invalid</div>
              <div className="stat-value">{stats.initial_valid_triage_invalid}</div>
            </div>
            <div className="stat-card">
              <div className="stat-title">Both Invalid</div>
              <div className="stat-value">{stats.both_invalid_cases}</div>
            </div>
          </div>
        </div>
      </div>
      <div className="stats-section">
        <h3 className="stats-section-title">Reviews per Doctor</h3>
        <div className="stat-chart-card review-per-doctor">
          <div className="stat-icon" style={{ marginBottom: 16 }}>
            <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
              <path fill="#4f46e5" d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4Zm0 2c-2.67 0-8 1.34-8 4v2c0 .55.45 1 1 1h14c.55 0 1-.45 1-1v-2c0-2.66-5.33-4-8-4Z" />
            </svg>
          </div>
          <ResponsiveContainer width="100%" height={120}>
            <BarChart data={reviewsPerDoctorData} margin={{ left: 10 }} barSize={28} barCategoryGap={32}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="value" radius={[6, 6, 0, 0]} className="alive">
                {reviewsPerDoctorData.map((entry, idx) => {
                  const colors = ['#0097B2', '#10b981', '#fbbf24'];
                  return <Cell key={`cell-${idx}`} fill={colors[idx % colors.length]} />;
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="stat-title">Reviews per Doctor</div>
        </div>
      </div>
    </div>
  );
};

export default GPAIStats;
